import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { UserPlus, Trash2, Edit, KeyRound, Search, Shield, User, Calendar, Copy, ArrowLeft, Users, Eye, Download, FileDown, IdCard, MessageCircle, Layers, ChevronDown, ChevronRight, Box, Grid3x3, Workflow } from 'lucide-react';
import { jsPDF } from 'jspdf';
import AdminSupportPanel from './AdminSupportPanel';
import AppsDataTab from './AppsDataTab';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user', subscription_end: '' });

  // Viewer leads (Share-link gate submissions)
  const [viewerLeads, setViewerLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // User Data (signup leads) tab
  const [userDataSearch, setUserDataSearch] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUsers(data.users || []);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchViewerLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/viewer-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewerLeads(data.viewer_leads || []);
    } catch {
      toast.error('Failed to load viewer data');
    }
    setLeadsLoading(false);
  }, [token]);

  useEffect(() => {
    if (activeTab === 'viewer-data') {
      fetchViewerLeads();
    }
  }, [activeTab, fetchViewerLeads]);

  const handleExportViewerData = async () => {
    try {
      const res = await fetch(`${API}/api/admin/viewer-data/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
      a.href = url;
      a.download = `viewer-data-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Viewer data exported');
    } catch {
      toast.error('Export failed');
    }
  };

  // ---- Per-user PDF for User Data tab ----
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch { return iso; }
  };
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); }
    catch { return iso; }
  };
  const isDemoExpired = (u) => {
    const end = u.subscription_end ? new Date(u.subscription_end) : null;
    return !!(end && end.getTime() < Date.now());
  };

  const handleDownloadUserPDF = (u) => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      // Header — brand bar
      doc.setFillColor(168, 85, 247); // fuchsia-500
      doc.rect(0, 0, pageW, 56, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text('AV Simulation Tool', 40, 26);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('Powered by Ideation Audio Visual', 40, 42);

      // Title
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('User Details Report', 40, 100);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(40, 110, pageW - 40, 110);

      // Body
      const rows = [
        ['Full Name', u.name || '—'],
        ['Email ID', u.email || '—'],
        ['Mobile Number', u.mobile || '—'],
        ['Country', u.country || '—'],
        ['City', u.city || '—'],
        ['Signup Date', fmtDate(u.subscription_start || u.created_at)],
        ['Expiry Date', fmtDate(u.subscription_end)],
        ['Status', isDemoExpired(u) ? 'Demo Expired' : 'Active'],
        ['Role', u.role || 'user'],
        ['Serial', u.serial || '—']
      ];
      let y = 140;
      doc.setFontSize(11);
      rows.forEach(([k, v]) => {
        doc.setFont(undefined, 'bold');
        doc.setTextColor(90, 90, 90);
        doc.text(`${k}`, 40, y);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(20, 20, 20);
        doc.text(`${v}`, 180, y);
        y += 22;
      });

      // Footer
      doc.setDrawColor(220, 220, 220);
      doc.line(40, y + 10, pageW - 40, y + 10);
      doc.setFontSize(9);
      doc.setTextColor(140, 140, 140);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 40, y + 28);
      doc.text('Confidential — admin-use only', pageW - 40, y + 28, { align: 'right' });

      const safe = (u.name || 'user').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      doc.save(`user-${safe}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    }
  };

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) { toast.error('Fill all required fields'); return; }
    try {
      const body = {
        name: form.name, email: form.email, password: form.password, role: form.role,
        subscription_end: form.subscription_end || new Date(Date.now() + 365 * 86400000).toISOString()
      };
      const res = await fetch(`${API}/api/admin/users`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) { toast.success('User created'); setShowAddDialog(false); setForm({ name: '', email: '', password: '', role: 'user', subscription_end: '' }); fetchUsers(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    try {
      const body = {};
      if (form.name) body.name = form.name;
      if (form.email) body.email = form.email;
      if (form.role) body.role = form.role;
      if (form.subscription_end) body.subscription_end = form.subscription_end;
      const res = await fetch(`${API}/api/admin/users/${editUser.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (res.ok) { toast.success('User updated'); setEditUser(null); fetchUsers(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This will remove all their data.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('User deleted'); fetchUsers(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleResetPassword = async (userId, email) => {
    if (!window.confirm(`Send password reset email to ${email}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/reset-password`, { method: 'POST', headers });
      const d = await res.json();
      toast.success(d.message);
    } catch { toast.error('Failed to send reset email'); }
  };

  const copySerial = (serial) => { navigator.clipboard.writeText(serial); toast.success('Serial copied'); };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.serial?.toLowerCase().includes(search.toLowerCase())
  );

  const isExpired = (u) => {
    if (u.role === 'admin') return false;
    if (!u.subscription_end) return true;
    return new Date() > new Date(u.subscription_end);
  };

  // Days until / since subscription end. Returns:
  //   { days: <int>, expired: <bool>, label: "<n> days <left|expired>", noEnd: <bool> }
  // Admins always show "—" because they have no expiry.
  const expireInfo = (u) => {
    if (u.role === 'admin') return { days: 0, expired: false, label: '—', noEnd: true };
    if (!u.subscription_end) return { days: 0, expired: true, label: 'No end date', noEnd: true };
    const end = new Date(u.subscription_end);
    const now = new Date();
    // Use whole-day diff (Math.ceil so a 14-hour-remaining sub still reads "1 day left").
    const ms = end.getTime() - now.getTime();
    const days = Math.ceil(Math.abs(ms) / 86400000);
    if (ms < 0) return { days, expired: true, label: `${days} day${days === 1 ? '' : 's'} expired`, noEnd: false };
    return { days, expired: false, label: `${days} day${days === 1 ? '' : 's'} left`, noEnd: false };
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" onClick={() => window.location.href = '/'} className="text-zinc-400 hover:text-white shrink-0" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-violet-400 shrink-0" /> <span className="truncate">Admin Panel</span>
              </h1>
              <p className="text-zinc-400 text-xs sm:text-sm mt-1">
                {activeTab === 'users'
                  ? `${users.length} users total`
                  : activeTab === 'user-data'
                  ? `${users.length} signup${users.length === 1 ? '' : 's'} captured`
                  : activeTab === 'apps-data'
                  ? 'All app projects per user'
                  : `${viewerLeads.length} viewer${viewerLeads.length === 1 ? '' : 's'} captured`}
              </p>
            </div>
          </div>
          {activeTab === 'users' ? (
            <Button onClick={() => { setForm({ name: '', email: '', password: '', role: 'user', subscription_end: '' }); setShowAddDialog(true); }} className="bg-violet-600 hover:bg-violet-700" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
          ) : activeTab === 'viewer-data' ? (
            <Button onClick={handleExportViewerData} className="bg-emerald-600 hover:bg-emerald-500" data-testid="export-viewer-data-btn" disabled={viewerLeads.length === 0}>
              <Download className="w-4 h-4 mr-2" /> Export to Excel
            </Button>
          ) : null}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-4 flex w-full overflow-x-auto sm:w-auto sm:inline-flex">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-violet-600/30 data-[state=active]:text-violet-200">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="user-data" data-testid="admin-tab-user-data" className="data-[state=active]:bg-emerald-600/30 data-[state=active]:text-emerald-200">
              <IdCard className="w-4 h-4 mr-2" /> User Data
            </TabsTrigger>
            <TabsTrigger value="apps-data" data-testid="admin-tab-apps-data" className="data-[state=active]:bg-amber-600/30 data-[state=active]:text-amber-200">
              <Layers className="w-4 h-4 mr-2" /> Apps Data
            </TabsTrigger>
            <TabsTrigger value="viewer-data" data-testid="admin-tab-viewer-data" className="data-[state=active]:bg-fuchsia-600/30 data-[state=active]:text-fuchsia-200">
              <Eye className="w-4 h-4 mr-2" /> Viewer Data
            </TabsTrigger>
            <TabsTrigger value="support" data-testid="admin-tab-support" className="data-[state=active]:bg-cyan-600/30 data-[state=active]:text-cyan-200">
              <MessageCircle className="w-4 h-4 mr-2" /> Support
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or serial..." className="pl-10 bg-zinc-900 border-zinc-700 text-white" data-testid="user-search-input" />
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading users...</div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                {/* Desktop / tablet — full table */}
                <div className="overflow-auto max-h-[calc(100vh-280px)] hidden sm:block" data-testid="users-table-scroll">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-zinc-900 shadow-[0_1px_0_0_rgb(39,39,42)]">
                      <tr className="border-b border-zinc-800 text-zinc-400 text-left">
                        <th className="p-3">#</th>
                        <th className="p-3">Name</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Serial</th>
                        <th className="p-3">Role</th>
                        <th className="p-3">Source</th>
                        <th className="p-3">Hours Used</th>
                        <th className="p-3">Subscription</th>
                        <th className="p-3">Expire Days</th>
                        <th className="p-3">Status</th>
                        <th className="p-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((u, i) => (
                        <tr key={u.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`user-row-${i}`}>
                          <td className="p-3 text-zinc-500">{i + 1}</td>
                          <td className="p-3 font-medium">{u.name}</td>
                          <td className="p-3 text-zinc-300">{u.email}</td>
                          <td className="p-3">
                            <span className="text-xs font-mono text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => copySerial(u.serial)} title="Click to copy">
                              {u.serial} <Copy className="w-3 h-3 inline ml-1" />
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.role === 'admin' ? 'bg-violet-500/20 text-violet-300' : 'bg-zinc-700 text-zinc-300'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="p-3" data-testid={`user-source-${i}`}>
                            {(() => {
                              const src = (u.signup_source || 'homepage').toLowerCase();
                              const label = src === 'simulation' ? 'Simulation'
                                : src === 'tpg' ? 'P&B'
                                : src === 'avd' ? 'AV Designer'
                                : src === 'self_signup' ? 'Direct'
                                : 'Homepage';
                              const cls = src === 'simulation' ? 'bg-violet-500/20 text-violet-300'
                                : src === 'tpg' ? 'bg-cyan-500/20 text-cyan-300'
                                : src === 'avd' ? 'bg-amber-500/20 text-amber-300'
                                : 'bg-zinc-700 text-zinc-300';
                              return <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${cls}`}>{label}</span>;
                            })()}
                          </td>
                          <td className="p-3 text-zinc-300 text-xs font-mono" data-testid={`user-hours-${i}`}
                              title={`Simulation: ${(((u.usage_seconds_simulation || 0) / 3600)).toFixed(1)} h\nPattern & Blend: ${(((u.usage_seconds_tpg || 0) / 3600)).toFixed(1)} h\nAV Designer: ${(((u.usage_seconds_avd || 0) / 3600)).toFixed(1)} h`}>
                            <div>{(((u.total_usage_seconds || 0) / 3600)).toFixed(1)} hrs</div>
                            <div className="flex gap-2 mt-1 text-[10px]">
                              <span className="text-violet-300/80" title="Simulation">S {(((u.usage_seconds_simulation || 0) / 3600)).toFixed(1)}</span>
                              <span className="text-cyan-300/80" title="Pattern & Blend">P {(((u.usage_seconds_tpg || 0) / 3600)).toFixed(1)}</span>
                              <span className="text-amber-300/80" title="AV Designer">A {(((u.usage_seconds_avd || 0) / 3600)).toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="p-3 text-zinc-400 text-xs">
                            {u.subscription_end ? new Date(u.subscription_end).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-3" data-testid={`user-expire-days-${i}`}>
                            {(() => {
                              const info = expireInfo(u);
                              if (info.noEnd) return <span className="text-xs text-zinc-500">{info.label}</span>;
                              const cls = info.expired
                                ? 'text-xs font-medium text-red-400'
                                : info.days <= 7
                                  ? 'text-xs font-medium text-amber-300'
                                  : 'text-xs font-medium text-emerald-400';
                              return <span className={cls}>{info.label}</span>;
                            })()}
                          </td>
                          <td className="p-3">
                            {isExpired(u) ? (
                              <span className="text-xs text-red-400 font-medium">Expired</span>
                            ) : (
                              <span className="text-xs text-emerald-400 font-medium">Active</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setEditUser(u); setForm({ name: u.name, email: u.email, password: '', role: u.role, subscription_end: u.subscription_end ? u.subscription_end.split('T')[0] : '' }); }} title="Edit" data-testid={`edit-user-${i}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u.id, u.email)} title="Reset Password" data-testid={`reset-pw-${i}`}>
                                <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                              </Button>
                              {u.role !== 'admin' && (
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id, u.name)} title="Delete" data-testid={`delete-user-${i}`}>
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filtered.length === 0 && <div className="text-center py-8 text-zinc-500">No users found</div>}
                </div>

                {/* Mobile — vertical card list */}
                <div className="block sm:hidden max-h-[calc(100vh-280px)] overflow-y-auto p-2 space-y-2" data-testid="users-mobile-list">
                  {filtered.map((u, i) => {
                    const src = (u.signup_source || 'homepage').toLowerCase();
                    const srcLabel = src === 'simulation' ? 'Simulation' : src === 'tpg' ? 'P&B' : src === 'avd' ? 'AV Designer' : src === 'self_signup' ? 'Direct' : 'Homepage';
                    const srcCls = src === 'simulation' ? 'bg-violet-500/20 text-violet-300' : src === 'tpg' ? 'bg-cyan-500/20 text-cyan-300' : src === 'avd' ? 'bg-amber-500/20 text-amber-300' : 'bg-zinc-700 text-zinc-300';
                    const info = expireInfo(u);
                    const expired = isExpired(u);
                    return (
                      <div key={u.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`user-card-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-white truncate">{u.name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.role === 'admin' ? 'bg-violet-500/20 text-violet-300' : 'bg-zinc-700 text-zinc-300'}`}>{u.role}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${srcCls}`}>{srcLabel}</span>
                              {expired ? (
                                <span className="text-[10px] text-red-400 font-medium">Expired</span>
                              ) : (
                                <span className="text-[10px] text-emerald-400 font-medium">Active</span>
                              )}
                            </div>
                            <div className="text-xs text-zinc-400 truncate mt-0.5">{u.email}</div>
                            <div
                              className="text-[11px] text-zinc-500 font-mono mt-1 cursor-pointer hover:text-zinc-300 inline-flex items-center"
                              onClick={() => copySerial(u.serial)}
                              title="Copy serial"
                            >
                              {u.serial} <Copy className="w-3 h-3 ml-1" />
                            </div>
                          </div>
                          <div className="shrink-0 flex flex-col items-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditUser(u); setForm({ name: u.name, email: u.email, password: '', role: u.role, subscription_end: u.subscription_end ? u.subscription_end.split('T')[0] : '' }); }} title="Edit" data-testid={`mobile-edit-user-${i}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleResetPassword(u.id, u.email)} title="Reset password" data-testid={`mobile-reset-pw-${i}`}>
                              <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                            </Button>
                            {u.role !== 'admin' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(u.id, u.name)} title="Delete" data-testid={`mobile-delete-user-${i}`}>
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                          <div>
                            <div className="text-zinc-500 uppercase tracking-wider text-[9px]">Hours</div>
                            <div className="text-zinc-300 font-mono">{(((u.total_usage_seconds || 0) / 3600)).toFixed(1)} h</div>
                            <div className="flex gap-1.5 mt-0.5 text-[9px]">
                              <span className="text-violet-300/80">S {(((u.usage_seconds_simulation || 0) / 3600)).toFixed(1)}</span>
                              <span className="text-cyan-300/80">P {(((u.usage_seconds_tpg || 0) / 3600)).toFixed(1)}</span>
                              <span className="text-amber-300/80">A {(((u.usage_seconds_avd || 0) / 3600)).toFixed(1)}</span>
                            </div>
                          </div>
                          <div>
                            <div className="text-zinc-500 uppercase tracking-wider text-[9px]">Subscription</div>
                            <div className="text-zinc-300">{u.subscription_end ? new Date(u.subscription_end).toLocaleDateString() : 'N/A'}</div>
                            <div className={info.expired ? 'text-red-400 font-medium' : info.days <= 7 ? 'text-amber-300 font-medium' : 'text-emerald-400 font-medium'}>
                              {info.label}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <div className="text-center py-8 text-zinc-500 text-sm">No users found</div>}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="user-data" className="mt-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={userDataSearch}
                onChange={(e) => setUserDataSearch(e.target.value)}
                placeholder="Search by name, email, mobile, country or city..."
                className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                data-testid="user-data-search"
              />
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading users...</div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                {/* Desktop / tablet — full table */}
                <div className="overflow-auto max-h-[calc(100vh-280px)] hidden sm:block" data-testid="user-data-table-scroll">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-zinc-900 shadow-[0_1px_0_0_rgb(39,39,42)]">
                      <tr className="border-b border-zinc-800 text-zinc-400 text-left">
                        <th className="p-3">#</th>
                        <th className="p-3">Name</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Mobile</th>
                        <th className="p-3">Country</th>
                        <th className="p-3">City</th>
                        <th className="p-3">Signup Date</th>
                        <th className="p-3">Expiry Date</th>
                        <th className="p-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users
                        .filter(u => {
                          const q = userDataSearch.toLowerCase();
                          if (!q) return true;
                          return (
                            (u.name || '').toLowerCase().includes(q) ||
                            (u.email || '').toLowerCase().includes(q) ||
                            (u.mobile || '').toLowerCase().includes(q) ||
                            (u.country || '').toLowerCase().includes(q) ||
                            (u.city || '').toLowerCase().includes(q)
                          );
                        })
                        .map((u, i) => (
                          <tr key={u.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`user-data-row-${i}`}>
                            <td className="p-3 text-zinc-500">{i + 1}</td>
                            <td className="p-3 font-medium">{u.name}</td>
                            <td className="p-3 text-zinc-300">{u.email}</td>
                            <td className="p-3 text-zinc-400 font-mono text-xs">{u.mobile || '—'}</td>
                            <td className="p-3 text-zinc-300">{u.country || '—'}</td>
                            <td className="p-3 text-zinc-300">{u.city || '—'}</td>
                            <td className="p-3 text-zinc-400 text-xs">{fmtDate(u.subscription_start || u.created_at)}</td>
                            <td className="p-3 text-xs">
                              <span className={isDemoExpired(u) ? 'text-red-400' : 'text-emerald-400'}>
                                {fmtDate(u.subscription_end)}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadUserPDF(u)}
                                className="border-zinc-700 hover:border-emerald-500 hover:text-emerald-300 text-xs h-7"
                                data-testid={`download-pdf-${i}`}
                              >
                                <FileDown className="w-3.5 h-3.5 mr-1" /> Download PDF
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="text-center py-10 text-zinc-500 text-sm">
                      No users yet. New signups will appear here automatically.
                    </div>
                  )}
                </div>

                {/* Mobile — vertical card list */}
                <div className="block sm:hidden max-h-[calc(100vh-280px)] overflow-y-auto p-2 space-y-2" data-testid="user-data-mobile-list">
                  {users
                    .filter(u => {
                      const q = userDataSearch.toLowerCase();
                      if (!q) return true;
                      return (
                        (u.name || '').toLowerCase().includes(q) ||
                        (u.email || '').toLowerCase().includes(q) ||
                        (u.mobile || '').toLowerCase().includes(q) ||
                        (u.country || '').toLowerCase().includes(q) ||
                        (u.city || '').toLowerCase().includes(q)
                      );
                    })
                    .map((u, i) => (
                      <div key={u.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`user-data-card-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-white truncate">{u.name}</div>
                            <div className="text-xs text-zinc-400 truncate">{u.email}</div>
                            <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{u.mobile || '—'}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadUserPDF(u)}
                            className="border-zinc-700 hover:border-emerald-500 hover:text-emerald-300 text-[11px] h-7 px-2 shrink-0"
                            data-testid={`mobile-download-pdf-${i}`}
                          >
                            <FileDown className="w-3 h-3 mr-1" /> PDF
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                          <div><span className="text-zinc-500">Country:</span> <span className="text-zinc-300">{u.country || '—'}</span></div>
                          <div><span className="text-zinc-500">City:</span> <span className="text-zinc-300">{u.city || '—'}</span></div>
                          <div><span className="text-zinc-500">Signup:</span> <span className="text-zinc-400">{fmtDate(u.subscription_start || u.created_at)}</span></div>
                          <div><span className="text-zinc-500">Expires:</span> <span className={isDemoExpired(u) ? 'text-red-400' : 'text-emerald-400'}>{fmtDate(u.subscription_end)}</span></div>
                        </div>
                      </div>
                    ))}
                  {users.length === 0 && (
                    <div className="text-center py-10 text-zinc-500 text-sm">No users yet.</div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="viewer-data" className="mt-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={leadsSearch}
                onChange={(e) => setLeadsSearch(e.target.value)}
                placeholder="Search by viewer name, email or project..."
                className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                data-testid="viewer-lead-search"
              />
            </div>

            {leadsLoading ? (
              <div className="text-center py-12 text-zinc-500">Loading viewer data...</div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                {/* Desktop / tablet — full table */}
                <div className="overflow-auto max-h-[calc(100vh-280px)] hidden sm:block" data-testid="viewer-data-table-scroll">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-zinc-900 shadow-[0_1px_0_0_rgb(39,39,42)]">
                      <tr className="border-b border-zinc-800 text-zinc-400 text-left">
                        <th className="p-3">#</th>
                        <th className="p-3">Name</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Mobile</th>
                        <th className="p-3">Project</th>
                        <th className="p-3">Date &amp; Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewerLeads
                        .filter(l => {
                          const q = leadsSearch.toLowerCase();
                          if (!q) return true;
                          return (
                            (l.name || '').toLowerCase().includes(q) ||
                            (l.email || '').toLowerCase().includes(q) ||
                            (l.project_name || '').toLowerCase().includes(q) ||
                            (l.mobile || '').toLowerCase().includes(q)
                          );
                        })
                        .map((l, i) => (
                        <tr key={l.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`viewer-lead-row-${i}`}>
                          <td className="p-3 text-zinc-500">{i + 1}</td>
                          <td className="p-3 font-medium">{l.name}</td>
                          <td className="p-3 text-zinc-300">{l.email}</td>
                          <td className="p-3 text-zinc-400 font-mono text-xs">{l.mobile || '—'}</td>
                          <td className="p-3 text-zinc-300">{l.project_name}</td>
                          <td className="p-3 text-zinc-400 text-xs">
                            {l.created_at ? new Date(l.created_at).toLocaleString() : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {viewerLeads.length === 0 && (
                    <div className="text-center py-10 text-zinc-500 text-sm">
                      No viewer data yet. Viewers who open a shareable link will appear here.
                    </div>
                  )}
                </div>

                {/* Mobile — vertical card list */}
                <div className="block sm:hidden max-h-[calc(100vh-280px)] overflow-y-auto p-2 space-y-2" data-testid="viewer-data-mobile-list">
                  {viewerLeads
                    .filter(l => {
                      const q = leadsSearch.toLowerCase();
                      if (!q) return true;
                      return (
                        (l.name || '').toLowerCase().includes(q) ||
                        (l.email || '').toLowerCase().includes(q) ||
                        (l.project_name || '').toLowerCase().includes(q) ||
                        (l.mobile || '').toLowerCase().includes(q)
                      );
                    })
                    .map((l, i) => (
                      <div key={l.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`viewer-lead-card-${i}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-white truncate">{l.name}</div>
                            <div className="text-xs text-zinc-400 truncate">{l.email}</div>
                          </div>
                          <span className="text-[10px] text-zinc-500 shrink-0">
                            {l.created_at ? new Date(l.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                        <div className="text-[11px] text-zinc-500 font-mono">{l.mobile || '—'}</div>
                        <div className="text-[11px] mt-1"><span className="text-zinc-500">Project: </span><span className="text-zinc-300">{l.project_name}</span></div>
                      </div>
                    ))}
                  {viewerLeads.length === 0 && (
                    <div className="text-center py-10 text-zinc-500 text-sm">No viewer data yet.</div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="support" className="mt-0">
            <AdminSupportPanel />
          </TabsContent>

          <TabsContent value="apps-data" className="mt-0">
            <AppsDataTab users={users} loading={loading} token={token} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-violet-400" /> Add User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300 text-sm">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="add-user-name" /></div>
            <div><Label className="text-zinc-300 text-sm">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="add-user-email" /></div>
            <div><Label className="text-zinc-300 text-sm">Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="add-user-password" /></div>
            <div><Label className="text-zinc-300 text-sm">Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-white rounded-md p-2 text-sm" data-testid="add-user-role">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div><Label className="text-zinc-300 text-sm">Subscription End</Label><Input type="date" value={form.subscription_end} onChange={(e) => setForm({ ...form, subscription_end: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="add-user-sub-end" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAdd} className="bg-violet-600 hover:bg-violet-700" data-testid="confirm-add-user-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-violet-400" /> Edit User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300 text-sm">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-user-name" /></div>
            <div><Label className="text-zinc-300 text-sm">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-user-email" /></div>
            <div><Label className="text-zinc-300 text-sm">Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-white rounded-md p-2 text-sm" data-testid="edit-user-role">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div><Label className="text-zinc-300 text-sm">Subscription End</Label><Input type="date" value={form.subscription_end} onChange={(e) => setForm({ ...form, subscription_end: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="edit-user-sub-end" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleUpdate} className="bg-violet-600 hover:bg-violet-700" data-testid="confirm-edit-user-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
